import { filedOrderEventId } from '@/app/document-review/filed-records'
import { proposalKindLabel, recordLabel } from '@/app/document-review/review-labels'
import type { DocumentReviewDetailOutput, DocumentReviewProposalView } from '@/app/document-review/use-document-review'

export function proposalShapeText(proposal: DocumentReviewProposalView): string {
  const record = proposal.record
  if (proposal.paymentId !== null) {
    const label = recordLabel(proposal.recordIdDisplay, proposal.paymentId)
    return record?.kind === 'attach_payment' ? `Filed on ${label}` : `Created ${label}`
  }
  const made = filedOrderEventId(proposal)
  if (made !== null) {
    const label = recordLabel(proposal.recordIdDisplay, made)
    if (record === null || record.kind !== 'attach') return `Created ${label}`
    return record.fileDocument === false ? `Updated ${label}` : `Joined ${label}`
  }
  if (record === null) return proposal.kind === 'trader_document' ? 'Files as an upload' : 'Asks a question'
  if (record.kind === 'attach_payment') {
    return `Files on ${recordLabel(proposal.recordIdDisplay, record.paymentId)} · changes nothing on it`
  }
  if (record.kind === 'attach') {
    const verb = record.fileDocument === false ? 'Updates' : 'Joins'
    return `${verb} ${recordLabel(proposal.recordIdDisplay, record.orderEventId)}`
  }
  if (proposal.kind === 'payable') return 'Creates a draft payable · bank accounts chosen later'
  return `Creates ${proposalKindLabel(proposal.kind)}`
}

export type OrderShape = { text: string; detail: string | null }

export function orderShape(detail: DocumentReviewDetailOutput): OrderShape {
  const order = detail.order
  if (order === null) return { text: 'No order named', detail: null }
  if (order.kind === 'existing') {
    return { text: `On order ${detail.orderDisplay ?? order.orderId}`, detail: null }
  }
  if (order.customer.kind === 'existing') {
    return {
      text: `Creates an order for ${detail.customerName ?? `customer ${order.customer.id}`}`,
      detail: order.description
    }
  }
  const party = order.customer.party
  const registry = party.allianzEhid === null ? '' : ` · registry id ${party.allianzEhid}`
  return {
    text: `Creates an order and the customer ${party.name}`,
    detail: [`New customer ${party.name} (${party.countryCode})${registry}`, order.description]
      .filter((part): part is string => part !== null)
      .join(' · ')
  }
}

export function supplierShapeText(proposal: DocumentReviewProposalView): string | null {
  const record = proposal.record
  if (record === null || record.kind !== 'create' || record.supplier === null) return null
  const supplier = record.supplier
  if (supplier.kind === 'existing') return proposal.supplierName ?? `Supplier ${supplier.id}`
  return `Creates the supplier ${supplier.party.name} (${supplier.party.countryCode})`
}

export function parentShapeText(
  proposal: DocumentReviewProposalView,
  proposals: readonly DocumentReviewProposalView[]
): string | null {
  const record = proposal.record
  if (record === null || record.kind !== 'create') return null
  if (record.parentOrderEventId !== null) {
    return `Under ${recordLabel(proposal.parentRecordDisplay, record.parentOrderEventId)}`
  }
  if (record.parentProposalOrdinal !== null) {
    const parent = proposals.find((one) => one.ordinal === record.parentProposalOrdinal)
    const kind = parent === undefined ? 'proposal' : proposalKindLabel(parent.kind)
    return `Under the ${kind} in proposal ${record.parentProposalOrdinal + 1}`
  }
  return null
}
